import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useAuth } from '../auth/useAuth'

const STORAGE_KEY = 'parsley-dark-mode'

interface ThemeContextValue {
  isDark: boolean
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [isDark, setIsDark] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true')
  const prevAuthRef = useRef(isAuthenticated)

  // Reset to light mode when the user signs out
  useEffect(() => {
    if (prevAuthRef.current && !isAuthenticated) {
      setIsDark(false)
      localStorage.removeItem(STORAGE_KEY)
    }
    prevAuthRef.current = isAuthenticated
  }, [isAuthenticated])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)
    if (isDark) {
      localStorage.setItem(STORAGE_KEY, 'true')
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [isDark])

  const toggleTheme = useCallback(() => setIsDark((prev) => !prev), [])

  return <ThemeContext.Provider value={{ isDark, toggleTheme }}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
